import { UnknownAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunkAction, RootState } from "../../../redux-store";
import * as cargoDialog from "../../../ducks/app/cargo-dialog";
import * as cargoDialogSelectors from "../../../ducks/app/cargo-dialog/selectors";
import * as scheduleTemplateDialog from "../../../ducks/app/schedule-cargo-template-dialog";
import * as scheduleTemplateDialogSelectors from "../../../ducks/app/schedule-cargo-template-dialog/selectors";
import * as clientTemplateDialog from "../../../ducks/app/client-cargo-template-dialog";
import * as clientTemplateDialogSelectors from "../../../ducks/app/client-cargo-template-dialog/selectors";
import { CargoData } from "../../../ducks/app/cargo-dialog/types";
import { CargoModalEntityType } from "./CargoModalEntityType";

/**
 * Where the open dialog's revision and conflict live, and how to fetch the
 * latest version, for each kind of entity the dialog edits (DORA-441). A cargo
 * template is a cargo row of its own, so it shares the cargo's socket room and
 * revision mechanics; only its endpoints and redux state differ.
 */
export type OpenCargoSyncSource = {
  selectLoadedRevision: (state: RootState) => string | null;
  selectRemoteConflict: (state: RootState) => boolean;
  // Null when the entity is gone (the dialog shows that by other means).
  fetchLatest: (
    dispatch: AppDispatch,
    id: string
  ) => Promise<{ data: CargoData; revision: string } | null>;
  revisionAdopted: (revision: string) => UnknownAction;
  remoteConflict: () => UnknownAction;
};

// The revision the open dialog is on, as the store has it right now: read
// inside callbacks, which must not see the value from their last render.
export const loadedRevisionOf =
  (source: OpenCargoSyncSource): AppThunkAction<string | null> =>
  (_dispatch, getState) =>
    source.selectLoadedRevision(getState());

export const cargoSyncSource: OpenCargoSyncSource = {
  selectLoadedRevision: cargoDialogSelectors.selectLoadedRevision,
  selectRemoteConflict: cargoDialogSelectors.selectRemoteConflict,
  fetchLatest: (dispatch, id) =>
    dispatch(cargoDialog.fetchLatestOpenCargo(id)).unwrap(),
  revisionAdopted: cargoDialog.openCargoRevisionAdopted,
  remoteConflict: cargoDialog.openCargoRemoteConflict,
};

const scheduleCargoTemplate: OpenCargoSyncSource = {
  selectLoadedRevision: scheduleTemplateDialogSelectors.selectLoadedRevision,
  selectRemoteConflict: scheduleTemplateDialogSelectors.selectRemoteConflict,
  fetchLatest: (dispatch, id) =>
    dispatch(scheduleTemplateDialog.fetchLatestOpenTemplate(id)).unwrap(),
  revisionAdopted: scheduleTemplateDialog.openTemplateRevisionAdopted,
  remoteConflict: scheduleTemplateDialog.openTemplateRemoteConflict,
};

// A client cargo template's endpoints take its own id, not its cargo row's,
// so its duck fetches the template it has open.
const clientCargoTemplate: OpenCargoSyncSource = {
  selectLoadedRevision: clientTemplateDialogSelectors.selectLoadedRevision,
  selectRemoteConflict: clientTemplateDialogSelectors.selectRemoteConflict,
  fetchLatest: (dispatch) =>
    dispatch(clientTemplateDialog.fetchLatestOpenTemplate()).unwrap(),
  revisionAdopted: clientTemplateDialog.openTemplateRevisionAdopted,
  remoteConflict: clientTemplateDialog.openTemplateRemoteConflict,
};

export const openCargoSyncSource = (
  entityType: CargoModalEntityType
): OpenCargoSyncSource | null => {
  switch (entityType) {
    case CargoModalEntityType.CARGO:
      return cargoSyncSource;
    case CargoModalEntityType.SCHEDULE_CARGO_TEMPLATE:
      return scheduleCargoTemplate;
    case CargoModalEntityType.CLIENT_CARGO_TEMPLATE:
      return clientCargoTemplate;
    default:
      return null;
  }
};
